import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";

import ContentEditor from "../../../components/ContentEditor";
import {
  getMotivationCodeContent,
  saveMotivationCodeContent,
} from "../services/api";

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });
}

export default function MotivationCodeContentPage() {
  const navigate = useNavigate();
  const { motivationCode } = useParams();
  const decodedCode = decodeURIComponent(motivationCode);
  const fileInputRef = useRef(null);

  const [bannerImage, setBannerImage] = useState("");
  const [puckContent, setPuckContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const response = await getMotivationCodeContent(decodedCode);
      if (response instanceof Error) throw response;
      const data = response.data;
      if (data) {
        setBannerImage(data.banner_image || "");
        setPuckContent(data.puck_content || "");
      } else {
        setBannerImage("");
        setPuckContent("");
      }
    } catch (err) {
      toast.error(err.message || "Failed to load content");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decodedCode]);

  async function persist({ banner = bannerImage, puck = puckContent } = {}) {
    setSaving(true);
    try {
      const response = await saveMotivationCodeContent({
        motivationCode: decodedCode,
        bannerImage: banner || null,
        puckContent: puck || null,
      });
      if (response instanceof Error) throw response;
      toast.success("Content saved");
    } catch (err) {
      toast.error(err.message || "Failed to save content");
    } finally {
      setSaving(false);
    }
  }

  async function handleBannerChange(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const base64 = await fileToBase64(file);
      setBannerImage(base64);
    } catch (err) {
      toast.error("Failed to read image file");
    }
  }

  async function savePuckContent(newContent) {
    setPuckContent(newContent);
    await persist({ puck: newContent });
  }

  return (
    <div className="w-full max-w-[1400px] mx-auto px-6 pb-8">
      <div className="w-full flex items-center justify-between py-6 border-b border-slate-200 mb-6">
        <div className="flex flex-col">
          <button
            type="button"
            onClick={() => navigate("/tools/motivation-codes")}
            className="text-slate-500 text-xs uppercase tracking-wider hover:text-blue-600 bg-transparent border-0 p-0 text-left"
          >
            ← Motivation Codes
          </button>
          <h1 className="text-slate-900 text-2xl font-bold tracking-tight m-0">
            Conteúdo ·{" "}
            <span className="font-mono text-blue-700">{decodedCode}</span>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            disabled={saving || loading}
            onClick={() => persist()}
          >
            {saving ? (
              <>
                <Spinner size="sm" className="me-2" />
                Salvando...
              </>
            ) : (
              "Salvar"
            )}
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => navigate("/tools/motivation-codes")}
          >
            Back
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="w-full text-center py-10">
          <Spinner />
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <Card className="border-0 shadow-sm">
            <div className="px-5 py-4 border-b border-slate-200">
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider m-0">
                Banner
              </h2>
              <p className="text-xs text-slate-500 mt-1 mb-0">
                Imagem exibida no topo da página de doação desse código.
              </p>
            </div>
            <div className="p-5">
              {bannerImage ? (
                <div className="flex flex-col gap-3">
                  <div className="rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
                    <img
                      src={bannerImage}
                      alt="Banner preview"
                      className="w-full max-h-[260px] object-contain"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Trocar imagem
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => setBannerImage("")}
                    >
                      Remover banner
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full flex flex-col items-center justify-center gap-2 py-10 px-4 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 hover:border-slate-400 transition-colors"
                >
                  <span className="text-3xl text-slate-400">+</span>
                  <span className="text-sm font-medium text-slate-700">
                    Clique para enviar uma imagem
                  </span>
                  <span className="text-xs text-slate-500">
                    PNG, JPG ou WebP · recomendado 1920×600
                  </span>
                </button>
              )}
              <Form.Control
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleBannerChange}
                className="d-none"
              />
            </div>
          </Card>

          <Card className="border-0 shadow-sm">
            <div className="px-5 py-4 border-b border-slate-200">
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider m-0">
                Conteúdo da página
              </h2>
              <p className="text-xs text-slate-500 mt-1 mb-0">
                Editor visual do conteúdo exibido abaixo do banner.
              </p>
            </div>
            <div className="p-5">
              <ContentEditor
                title={decodedCode}
                content={puckContent}
                setContent={savePuckContent}
              />
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
